import { Component, Input, Inject, Output, EventEmitter, OnInit, OnChanges } from 'ng-metadata/core';
import './user-menu.component.scss';
import { AppContainerStateService } from '../app-container-state.service';
import { AppContainerStateChangeName } from '../shared/shared.interfaces';

interface UserInput {
    email: string;
    firstName: string;
    lastName: string;
}

/**
 * @ngdoc directive
 * @name mwUserMenu
 * @module mw.components.appContainer
 *
 * @param {object} [user] - The user object with all the necessary user information. Example:
 *
 * <hljs hljs-language="js">
 *   {
 *     firstName: 'firstName',
 *     lastName: 'lastName',
 *     email: 'email@email.com'
 *   }
 * </hljs>
 *
 * @param {boolean} [hide-help=false] - Determines whether the Help menu item should be hidden.
 * @param {string} [main-header-url] - The URL to follow on click of the user settings menu item.
 * @param {expression} [on-help] - Angular expression to evaluate when the user clicks the Help menu item. $event is the mw-menu-item click event data.
 * @param {expression} [on-logout] - Angular expression to evaluate when the user clicks the Logout menu item. $event is the mw-menu-item click event data.
 *
 * @description
 * `<mw-user-menu>` is a component used to display a user menu.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-user-menu user="$ctrl.user" on-logout="$ctrl.onLogout($event)"></mw-user-menu>
 *
 *   <!-- Custom Menu Items -->
 *   <mw-user-menu user="$ctrl.user" on-logout="$ctrl.onLogout($event)">
 *     <mw-menu-item on-click="$ctrl.onClickHandler()">Click Me</mw-menu-item>
 *   </mw-user-menu>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-user-menu',
    template: `
        <md-menu class="mw-menu" md-no-padding="md-no-padding" md-position-mode="target-right target" md-offset="0 50">
            <md-button id="mw-user-menu__trigger-button" aria-label="{{'Open Menu' | translate}}" class="mw-user-menu__trigger-button md-icon-button _md-no-style" ng-click="$ctrl.open($mdOpenMenu, $event)" md-no-ink>
                <md-tooltip class="mw-user-menu__trigger-button-tooltip" md-direction="bottom" md-delay="2000" md-visible="$ctrl.isTooltipVisible">
                {{$ctrl.user.firstName + ' ' + $ctrl.user.lastName}}<br/>
                ({{$ctrl.user.email}})
                </md-tooltip>
                <div class="mw-user-menu__trigger-button-text">{{$ctrl.initials}}</div>
            </md-button>
            <md-menu-content id="mw-user-menu-content" class="mw-menu-dropdown" width="4" md-menu-align-target>
                <mw-menu-item type="focusdummy" md-autofocus></mw-menu-item>
                <mw-menu-item auto-height="true" is-disabled="true" class="mw-menu-item--default-color">
                    <div layout="row" layout-align="start center" class="mw-user-menu__user">
                        <div flex="none" class="mw-user-menu__user-avatar">{{$ctrl.initials}}</div>
                        <div flex="inital">
                            <div class="mw-user-menu__user-name md-subhead">{{$ctrl.user.firstName + ' ' + $ctrl.user.lastName}}</div>
                            <div class="mw-user-menu__user-email md-caption">{{$ctrl.user.email}}</div>
                        </div>
                    </div>
                </mw-menu-item>
                <md-divider class="mw-menu-divider"></md-divider>
                <ng-transclude>
                    <mw-menu-item ng-if="$ctrl.user && $ctrl.user.isInternal" class="mw-user-menu__mobile-demo-button" on-click="$ctrl.openMobileAppDemo()" aria-label="Mobile App Demo">Mobile App Demo</mw-menu-item>
                    <mw-menu-item class="mw-user-menu__setting-button" aria-label="{{'Personal Settings' | translate}}" url="{{$ctrl.mainHeaderUrl}}"><span translate>Personal Settings</span></mw-menu-item>
                    <mw-menu-item class="mw-user-menu__help-button" aria-label="{{'Help' | translate}}" ng-hide="$ctrl.hideHelp" on-click="$ctrl.help($event)" hide-gt-xs><span translate>Help</span></mw-menu-item>
                </ng-transclude>
                <mw-menu-item class="mw-user-menu__logout-button" aria-label="{{'Logout' | translate}}" on-click="$ctrl.logout($event)"><span translate>Logout</span></mw-menu-item>
            </md-menu-content>
        </md-menu>
    `,
    legacy: {
        transclude: true
    }
})
export class UserMenuComponent implements OnInit, OnChanges {
    @Input('<') user: UserInput;
    @Input('<') hideHelp: boolean;
    @Input('@') mainHeaderUrl: string;
    @Output() onHelp = new EventEmitter<ng.IAngularEvent>();
    @Output() onLogout = new EventEmitter<ng.IAngularEvent>();

    private initials: string;
    private fullName: string;
    private isTooltipVisible: boolean = false;

    constructor(
        @Inject('mwAppContainerStateService') private mwAppContainerStateService: AppContainerStateService,
        @Inject('$window') private $window
    ) {
        'ngInject';
    }

    ngOnInit() {
        this.initials = this.getInitials(this.user, true);
        this.fullName = this.getFullName(this.user);
    }

    ngOnChanges(changes) {
        if (changes && changes.user && !changes.user.isFirstChange()) {
            this.initials = this.getInitials(this.user, true);
        }
    }

    open($mdOpenMenu, $event: ng.IAngularEvent) {
        $mdOpenMenu($event);
        this.isTooltipVisible = false;
    }

    getInitials(user: UserInput, shouldSetStateCookie?: boolean): string {
        const _user = user || this.user;
        const initials = _user ?
                        (_user.firstName !== undefined && _user.firstName.length > 0 ? _user.firstName[0] : '') +
                        (_user.lastName !== undefined && _user.lastName.length > 0 ? _user.lastName[0] : '')
                        : '';

        if (shouldSetStateCookie) {
            this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.initials, initials);
        }

        return initials;
    }

    getFullName(user: UserInput): string {
        const _user = user || this.user;
        return _user ?
            ((_user.firstName !== undefined && _user.firstName.length > 0 ? _user.firstName : '') + ' ' +
            (_user.lastName !== undefined && _user.lastName.length > 0 ? _user.lastName : '')).trim()
         : '';
    }

    help(event: ng.IAngularEvent) {
        this.onHelp.emit(event);
    }

    logout(event: ng.IAngularEvent) {
        this.mwAppContainerStateService.removeStateCookie();
        this.onLogout.emit(event);
    }

    openMobileAppDemo() {
        this.$window.open('https://www.meltwater.com/mobile-demo?autologin', '_blank')
    }

}



// WEBPACK FOOTER //
// ./src/lib/components/app-container/user-menu/user-menu.component.ts